import { useEffect, useState } from 'react';
import { useAuth } from './auth-context';
import { useNavigate } from 'react-router-dom';
import { checkIsAdmin } from './admin-utils';

/**
 * React hook to protect routes/pages for admin-only access.
 * Redirects to /login if not authenticated, or / if not admin.
 * Returns { isAdmin, loading, user, profile } for convenience.
 */
export function useRequireAdmin() {
  const { user, profile, isLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  useEffect(() => {
    const checkAdminAccess = async () => {
      if (isLoading) return;
      
      if (!user) {
        navigate('/login?redirect=' + encodeURIComponent(window.location.pathname), { replace: true });
        return;
      }

      try {
        setCheckingAdmin(true);
        // Use the robust admin checking function instead of relying on profile
        const adminStatus = await checkIsAdmin();
        setIsAdmin(adminStatus);
        
        if (!adminStatus) {
          navigate('/', { replace: true });
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        navigate('/', { replace: true });
      } finally {
        setCheckingAdmin(false);
      }
    };

    checkAdminAccess();
  }, [user, isLoading, navigate]);

  return { 
    isAdmin, 
    loading: isLoading || checkingAdmin, 
    user, 
    profile 
  };
}
